import { Link } from "react-router-dom";
import { Shell } from "../components/Shell";

/**
 * Política de privacidad y Habeas Data — PLANTILLA base.
 * Cada alcaldía (responsable del tratamiento) debe adaptarla y validarla
 * con su área jurídica antes de publicarla en el portal ciudadano.
 * MiPueblo actúa como ENCARGADO del tratamiento, no como responsable.
 *
 * Marco legal: Constitución Art. 15, Ley 1581/2012, Decreto 1377/2013,
 * Ley 1755/2015 y vigilancia de la SIC.
 */
export function DocsHabeasDataPage() {
  return (
    <Shell>
      <section className="page" style={{ maxWidth: 880 }}>
        <div className="eyebrow">
          <Link to="/docs">← Documentación</Link> · Política de privacidad y
          Habeas Data
        </div>

        <h1 style={{ margin: "8px 0 12px", fontSize: 32 }}>
          Política de privacidad y Habeas Data
        </h1>

        <div className="callout warn">
          <strong>Esto es una PLANTILLA.</strong> Cada alcaldía debe revisarla
          con su área jurídica antes de publicarla. MiPueblo no presta asesoría
          legal; entregamos el texto base sobre el cual cada cliente construye
          su política definitiva.
        </div>

        <div className="callout" style={{ marginTop: 12 }}>
          <strong>Marco legal de referencia:</strong> Constitución Política
          Art. 15, Ley 1581 de 2012 (Régimen general de protección de datos
          personales), Decreto 1377 de 2013 (reglamentación parcial), Ley 1755
          de 2015 (derecho de petición) y vigilancia de la{" "}
          <strong>Superintendencia de Industria y Comercio (SIC)</strong>.
        </div>

        {/* ---- 1 ---- */}
        <Section title="1. Responsable del tratamiento">
          <p>
            El <strong>responsable del tratamiento</strong> de los datos
            personales recolectados a través de la plataforma es la{" "}
            <strong>Alcaldía de [Municipio]</strong>, identificada con NIT
            [NIT], con dirección [dirección oficial], correo electrónico
            [correo institucional] y teléfono [conmutador].
          </p>
          <p>
            La alcaldía, en su condición de entidad pública, recolecta y trata
            los datos personales de los ciudadanos para el cumplimiento de sus
            funciones constitucionales y legales, en particular la atención de
            peticiones, quejas, reclamos, sugerencias, denuncias y felicitaciones
            (PQRSDF).
          </p>
          <div className="callout">
            <strong>Aviso de relación responsable/encargado:</strong> MiPueblo
            S.A.S. actúa como <strong>ENCARGADO</strong> del tratamiento por
            cuenta y orden de la alcaldía. La plataforma tecnológica es operada
            por MiPueblo, pero la responsabilidad legal sobre los datos
            recolectados recae en la alcaldía. Existe un contrato de
            transmisión de datos entre ambas partes que regula esta relación
            conforme al Art. 25 del Decreto 1377/2013.
          </div>
        </Section>

        {/* ---- 2 ---- */}
        <Section title="2. Datos personales que se recolectan">
          <p>
            Dependiendo del trámite o canal utilizado por el ciudadano, la
            plataforma puede recolectar las siguientes categorías de datos:
          </p>
          <Table
            head={["Categoría", "Datos específicos"]}
            rows={[
              [
                "Identificación",
                "Nombres y apellidos, tipo y número de documento (CC, CE, TI, NIT, PA)",
              ],
              [
                "Contacto",
                "Correo electrónico, número de celular, dirección física, municipio",
              ],
              [
                "Contenido de la solicitud",
                "Asunto, descripción de la PQRSDF, dependencia destinataria, categoría",
              ],
              [
                "Documentos adjuntos",
                "Archivos en PDF, imagen, audio o video que el ciudadano cargue como soporte",
              ],
              [
                "Datos de chat",
                "Conversaciones con operadores o con el asistente automático del portal",
              ],
              [
                "Datos de navegación",
                "Dirección IP, tipo de dispositivo, navegador, fecha y hora de acceso, cookies técnicas",
              ],
              [
                "Datos de georreferenciación",
                "Coordenadas aproximadas cuando el ciudadano las aporta para reportar un hecho (opcional)",
              ],
            ]}
          />
          <p style={{ marginTop: 12 }}>
            La plataforma <strong>no solicita datos sensibles</strong> (origen
            étnico, orientación sexual, convicciones políticas, religiosas,
            salud, datos biométricos) salvo cuando sean indispensables para
            atender la solicitud y previa autorización expresa del titular,
            conforme al Art. 6 de la Ley 1581/2012.
          </p>
        </Section>

        {/* ---- 3 ---- */}
        <Section title="3. Finalidades del tratamiento">
          <p>Los datos se tratan exclusivamente para las siguientes finalidades:</p>
          <Ul
            items={[
              "Recibir, radicar, clasificar, asignar y dar respuesta a PQRSDF dentro de los plazos legales (Ley 1755/2015).",
              "Atender consultas a través del chat ciudadano y canales digitales habilitados por la alcaldía.",
              "Notificar al ciudadano el estado, traslado o cierre de su solicitud por los medios que él autorizó.",
              "Cumplir con el deber de archivo y conservación documental (Ley 594/2000).",
              "Generar estadísticas internas, indicadores de gestión y reportes a entes de control en forma agregada y anonimizada.",
              "Mejorar la calidad del servicio y entrenar modelos de asistencia automatizada cuando el ciudadano lo haya autorizado.",
              "Cumplir requerimientos judiciales, administrativos o de entes de control que legalmente lo exijan.",
            ]}
          />
        </Section>

        {/* ---- 4 ---- */}
        <Section title="4. Derechos del titular">
          <p>
            En desarrollo del Art. 15 de la Constitución y del Art. 8 de la Ley
            1581/2012, el ciudadano titular de los datos personales tiene
            derecho a:
          </p>
          <Ul
            items={[
              <>
                <strong>Conocer</strong> los datos personales que la alcaldía
                tiene sobre él y solicitar prueba de la autorización otorgada.
              </>,
              <>
                <strong>Actualizar y rectificar</strong> los datos que sean
                parciales, inexactos, incompletos, fraccionados o que induzcan a
                error.
              </>,
              <>
                <strong>Solicitar la supresión</strong> de los datos cuando el
                tratamiento no se ajuste a la ley o se haya superado la
                finalidad.
              </>,
              <>
                <strong>Revocar la autorización</strong> otorgada para el
                tratamiento, salvo cuando exista un deber legal o contractual
                que obligue a conservarlos.
              </>,
              <>
                <strong>Presentar quejas</strong> ante la{" "}
                <strong>Superintendencia de Industria y Comercio (SIC)</strong>{" "}
                por infracciones a la ley de protección de datos.
              </>,
              <>
                <strong>Acceder en forma gratuita</strong> a sus datos
                personales que hayan sido objeto de tratamiento.
              </>,
            ]}
          />
        </Section>

        {/* ---- 5 ---- */}
        <Section title="5. Cómo ejercer los derechos">
          <p>
            El titular o su causahabiente puede ejercer sus derechos a través
            de los siguientes canales:
          </p>
          <Table
            rows={[
              ["Correo electrónico", "[habeasdata@municipio.gov.co]"],
              [
                "Portal ciudadano",
                "Formulario en {slug}.mipuebloapp.com/habeas-data",
              ],
              ["Presencial", "Ventanilla de atención al ciudadano de la alcaldía"],
              ["Correspondencia física", "Dirección oficial de la alcaldía"],
            ]}
          />
          <p style={{ marginTop: 12 }}>
            <strong>Formato mínimo de la solicitud:</strong>
          </p>
          <Ul
            items={[
              "Nombres y apellidos completos del titular.",
              "Tipo y número de documento de identidad.",
              "Datos de contacto (correo y/o dirección para notificación).",
              "Descripción clara del derecho que se ejerce (consulta, rectificación, supresión, revocatoria).",
              "Hechos que dan lugar a la solicitud y documentos que la soporten.",
            ]}
          />
          <div className="callout" style={{ marginTop: 14 }}>
            <strong>Plazos legales de respuesta (Ley 1581/2012):</strong>
          </div>
          <Table
            head={["Tipo", "Plazo máximo", "Prórroga"]}
            rows={[
              ["Consultas (Art. 14)", "10 días hábiles", "+5 días hábiles"],
              [
                "Reclamos (Art. 15) — corrección, actualización, supresión, revocatoria",
                "15 días hábiles",
                "+8 días hábiles",
              ],
            ]}
          />
          <p style={{ marginTop: 10 }}>
            Si la solicitud está incompleta, la alcaldía requerirá al titular
            dentro de los 5 días siguientes para que la subsane. Pasados dos
            meses sin subsanar, se entenderá desistida.
          </p>
        </Section>

        {/* ---- 6 ---- */}
        <Section title="6. Encargado del tratamiento — MiPueblo S.A.S.">
          <p>
            MiPueblo S.A.S. opera la infraestructura tecnológica que aloja y
            procesa los datos recolectados por la alcaldía. En su condición de
            encargado:
          </p>
          <Ul
            items={[
              "Trata los datos únicamente conforme a las instrucciones de la alcaldía.",
              "No utiliza ni cede los datos para fines comerciales propios.",
              "Garantiza la confidencialidad incluso después de finalizada la relación contractual.",
              "Devuelve o destruye los datos al término del contrato, según instrucción del responsable.",
              "Notifica a la alcaldía cualquier incidente de seguridad en un plazo máximo de 48 horas desde su detección.",
            ]}
          />
          <Table
            rows={[
              ["Razón social", "MiPueblo S.A.S."],
              ["NIT", "[NIT del encargado]"],
              ["Domicilio", "[Dirección]"],
              ["Correo de contacto", "habeasdata@mipuebloapp.com"],
            ]}
          />
        </Section>

        {/* ---- 7 ---- */}
        <Section title="7. Transferencias y transmisiones de datos">
          <p>
            La alcaldía puede compartir datos personales del ciudadano en los
            siguientes escenarios:
          </p>
          <Ul
            items={[
              "Con dependencias internas de la administración municipal cuando el trámite así lo requiera (traslado por competencia).",
              "Con otras entidades públicas cuando el peticionario haya errado en la destinataria y proceda traslado por competencia (Art. 21 Ley 1755/2015).",
              "Con entes de control (Procuraduría, Contraloría, Personería, Defensoría) cuando estos lo requieran.",
              "Con autoridades judiciales en cumplimiento de orden judicial.",
              <>
                Con <strong>MiPueblo S.A.S.</strong> en su condición de
                encargado del tratamiento.
              </>,
              "Con proveedores de servicios cloud (alojamiento, backup, monitoreo) sujetos a contratos de confidencialidad y estándares de seguridad equivalentes.",
            ]}
          />
          <p>
            Cualquier transferencia internacional de datos cumple con el
            estándar del Art. 26 de la Ley 1581/2012 y solo se realiza hacia
            países con nivel adecuado de protección o bajo cláusulas
            contractuales tipo aprobadas.
          </p>
        </Section>

        {/* ---- 8 ---- */}
        <Section title="8. Tiempo de conservación">
          <p>
            Los datos se conservan por el tiempo necesario para cumplir la
            finalidad para la que fueron recolectados y, en todo caso, por los
            términos que exijan las normas de archivo y la Ley 594/2000.
          </p>
          <Table
            head={["Dato", "Tiempo mínimo", "Fundamento"]}
            rows={[
              [
                "PQRSDF radicada y su respuesta",
                "10 años",
                "Archivo histórico estatal — Ley 594/2000 + tablas de retención documental",
              ],
              [
                "Adjuntos a la PQRSDF",
                "10 años",
                "Acompañan al expediente principal",
              ],
              [
                "Conversaciones de chat informal (sin radicado)",
                "2 años desde el cierre, luego anonimización",
                "Finalidad de mejora del servicio",
              ],
              [
                "Audit log (trazabilidad)",
                "5 años",
                "Auditoría interna y entes de control",
              ],
              [
                "Datos del peticionario sin radicados activos",
                "1 año, luego solicitud de borrado o anonimización",
                "Principio de finalidad — Art. 4 Ley 1581/2012",
              ],
            ]}
          />
          <p style={{ marginTop: 10 }}>
            Para el detalle completo de retención por tipo de dato, ver la{" "}
            <Link to="/docs/retencion">Política de retención de datos</Link>.
          </p>
        </Section>

        {/* ---- 9 ---- */}
        <Section title="9. Medidas de seguridad">
          <p>
            La plataforma implementa medidas técnicas, humanas y administrativas
            para proteger los datos contra adulteración, pérdida, consulta, uso
            o acceso no autorizado, conforme al Art. 4 lit. g) de la Ley
            1581/2012:
          </p>
          <Ul
            items={[
              <>
                <strong>Aislamiento multi-tenant</strong> a nivel de base de
                datos con Row Level Security (RLS) de PostgreSQL: cada alcaldía
                solo accede a sus propios datos.
              </>,
              <>
                <strong>Encriptación en tránsito</strong>: todo el tráfico viaja
                sobre TLS 1.2+.
              </>,
              <>
                <strong>Encriptación en reposo</strong>: bases de datos y
                backups encriptados con AES-256.
              </>,
              <>
                <strong>Autenticación robusta</strong>: JWT de corta duración,
                refresh tokens rotativos y soporte de doble factor (MFA) para
                cuentas administrativas.
              </>,
              <>
                <strong>Audit log</strong>: registro inmutable de quién accedió
                a qué dato y cuándo.
              </>,
              <>
                <strong>Control de acceso por roles</strong> (super_admin,
                admin_tenant, supervisor, operador) con principio de mínimo
                privilegio.
              </>,
              <>
                <strong>Backups</strong> automáticos diarios con retención de 90
                días y pruebas de restauración periódicas.
              </>,
              <>
                <strong>Monitoreo</strong> continuo y alertas ante accesos
                anómalos.
              </>,
            ]}
          />
        </Section>

        {/* ---- 10 ---- */}
        <Section title="10. Modificaciones a la política">
          <p>
            La alcaldía puede modificar esta política en cualquier momento. Los
            cambios sustanciales serán comunicados al titular por correo
            electrónico, mediante aviso en el portal ciudadano o por cualquier
            medio idóneo, con al menos 10 días de anticipación a su entrada en
            vigencia. La fecha de la última actualización quedará indicada al
            pie del documento publicado.
          </p>
        </Section>

        {/* ---- 11 ---- */}
        <Section title="11. Aviso de Habeas Data que el ciudadano acepta">
          <p>
            Plantilla del aviso que se muestra al ciudadano antes de radicar una
            PQRSDF o iniciar un chat. Debe estar acompañado de un checkbox de
            aceptación expresa:
          </p>
          <pre className="code-block" style={{ whiteSpace: "pre-wrap" }}>
{`Autorizo a la Alcaldía de [Municipio], identificada con NIT [NIT], en
calidad de responsable del tratamiento, y a MiPueblo S.A.S. como
encargado, para recolectar, almacenar, usar, circular y suprimir mis
datos personales con la finalidad de atender mi solicitud, dar respuesta
en los plazos legales (Ley 1755/2015), generar estadísticas de gestión y
cumplir con obligaciones legales de archivo.

He sido informado de mis derechos como titular (conocer, actualizar,
rectificar, suprimir, revocar la autorización y presentar quejas ante la
SIC) y de los canales disponibles para ejercerlos, descritos en la
política de privacidad disponible en {slug}.mipuebloapp.com/privacidad.

Declaro que la información que entrego es veraz y que soy el titular de
los datos o cuento con autorización del titular para suministrarlos.

[ ] Acepto el tratamiento de mis datos personales conforme a la política
    de privacidad.`}
          </pre>
          <div className="callout tip" style={{ marginTop: 12 }}>
            La plataforma registra en el audit log: fecha, hora, IP, versión del
            aviso aceptada y método de autenticación. Esta evidencia es la
            prueba de la autorización exigida por el Art. 9 de la Ley 1581/2012.
          </div>
        </Section>

        {/* ---- 12 ---- */}
        <Section title="12. Procedimiento interno para solicitudes de eliminación">
          <p>
            Cuando un ciudadano solicita la supresión o revocatoria, el flujo
            interno es:
          </p>
          <Ol>
            <li>
              <strong>Recepción</strong>: la solicitud entra por uno de los
              canales habilitados y se asigna automáticamente al rol{" "}
              <code>admin_tenant</code> o al funcionario designado como{" "}
              <strong>oficial de protección de datos</strong>.
            </li>
            <li>
              <strong>Validación de identidad</strong>: se confirma que quien
              solicita es el titular o su representante legal, mediante
              documento de identidad y, cuando aplique, poder.
            </li>
            <li>
              <strong>Revisión de legitimidad</strong>: se verifica si existe un
              deber legal o contractual que obligue a conservar los datos
              (ejemplo: PQRSDF dentro del periodo de archivo de 10 años, litigio
              en curso, requerimiento judicial). Si existe, se informa al
              titular y se documenta la causal.
            </li>
            <li>
              <strong>Ejecución</strong>: si procede la supresión, se ejecuta el
              borrado o anonimización en la plataforma. El sistema permite
              marcar registros como <code>suprimidos_titular</code> manteniendo
              la trazabilidad del acto sin exponer los datos personales.
            </li>
            <li>
              <strong>Notificación al titular</strong>: se responde por escrito
              dentro de los plazos legales (15 días hábiles + prórroga).
            </li>
            <li>
              <strong>Audit log</strong>: el sistema registra automáticamente
              qué dato se eliminó, quién lo eliminó, cuándo y bajo qué número de
              radicado de la solicitud.
            </li>
          </Ol>
        </Section>

        <div className="callout warn" style={{ marginTop: 32 }}>
          <strong>Recordatorio final:</strong> este texto es una plantilla de
          referencia técnica. La alcaldía es la única responsable de adaptarla,
          aprobarla legalmente y publicarla en su portal ciudadano. MiPueblo no
          presta asesoría jurídica.
        </div>
      </section>
    </Shell>
  );
}

/* ============ helpers de presentación ============ */

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        marginTop: 32,
        paddingTop: 24,
        borderTop: "1px solid var(--hairline)",
      }}
    >
      <h2 style={{ fontSize: 20, marginTop: 0, marginBottom: 14 }}>{title}</h2>
      <div style={{ lineHeight: 1.6, color: "var(--ink-2)", fontSize: 14.5 }}>
        {children}
      </div>
    </section>
  );
}

function Ol({ children }: { children: React.ReactNode }) {
  return (
    <ol style={{ paddingLeft: 22, margin: "8px 0", display: "grid", gap: 6 }}>
      {children}
    </ol>
  );
}

function Ul({ items }: { items: React.ReactNode[] }) {
  return (
    <ul style={{ paddingLeft: 22, margin: "8px 0", display: "grid", gap: 4 }}>
      {items.map((it, i) => (
        <li key={i}>{it}</li>
      ))}
    </ul>
  );
}

function Table({
  head,
  rows,
}: {
  head?: string[];
  rows: (string | React.ReactNode)[][];
}) {
  return (
    <div
      style={{
        marginTop: 10,
        border: "1px solid var(--hairline)",
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
        {head && (
          <thead>
            <tr style={{ background: "var(--bone-warm)" }}>
              {head.map((h, i) => (
                <th
                  key={i}
                  style={{
                    textAlign: "left",
                    padding: "8px 12px",
                    fontFamily: "var(--font-mono)",
                    fontSize: 10.5,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "var(--ink-3)",
                    fontWeight: 500,
                    borderBottom: "1px solid var(--hairline)",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              style={{ borderTop: i === 0 && !head ? 0 : "1px solid var(--hairline)" }}
            >
              {row.map((cell, j) => (
                <td
                  key={j}
                  style={{
                    padding: "10px 12px",
                    verticalAlign: "top",
                    fontWeight: j === 0 ? 600 : 400,
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
